import React, { useState, useEffect } from 'react';
import { Save, RefreshCw, Award, BookOpen, ChevronDown, ChevronUp } from 'lucide-react';
import { logisticsTips } from '../data/logisticsTips';
import { LogisticsTip, TipRarity } from '../types';

// Colores de fondo para cada nivel de rareza
const rarityColors = {
  [TipRarity.COMMON]: 'bg-white border-gray-200',
  [TipRarity.UNCOMMON]: 'bg-green-50 border-green-300',
  [TipRarity.RARE]: 'bg-blue-50 border-blue-300',
  [TipRarity.EPIC]: 'bg-purple-50 border-purple-300',
  [TipRarity.LEGENDARY]: 'bg-amber-50 border-amber-300'
};

// Colores de texto para cada nivel de rareza
const rarityTextColors = {
  [TipRarity.COMMON]: 'text-gray-800',
  [TipRarity.UNCOMMON]: 'text-green-700',
  [TipRarity.RARE]: 'text-blue-700',
  [TipRarity.EPIC]: 'text-purple-700',
  [TipRarity.LEGENDARY]: 'text-amber-700'
};

// Colores de badge para cada nivel de rareza
const rarityBadgeColors = {
  [TipRarity.COMMON]: 'bg-gray-100 text-gray-800 border border-gray-200',
  [TipRarity.UNCOMMON]: 'bg-green-100 text-green-800 border border-green-200',
  [TipRarity.RARE]: 'bg-blue-100 text-blue-800 border border-blue-200',
  [TipRarity.EPIC]: 'bg-purple-100 text-purple-800 border border-purple-200',
  [TipRarity.LEGENDARY]: 'bg-amber-100 text-amber-800 border border-amber-200'
};

// Gradientes para cada nivel de rareza
const rarityGradients = {
  [TipRarity.COMMON]: 'from-gray-50 to-white',
  [TipRarity.UNCOMMON]: 'from-green-50 to-green-100',
  [TipRarity.RARE]: 'from-blue-50 to-blue-100',
  [TipRarity.EPIC]: 'from-purple-50 to-purple-100',
  [TipRarity.LEGENDARY]: 'from-amber-50 to-amber-100'
};

// Sombras para cada nivel de rareza
const rarityShadows = {
  [TipRarity.COMMON]: '',
  [TipRarity.UNCOMMON]: 'shadow-green-100',
  [TipRarity.RARE]: 'shadow-blue-100',
  [TipRarity.EPIC]: 'shadow-purple-100',
  [TipRarity.LEGENDARY]: 'shadow-amber-200 shadow-md'
};

const LogisticsTips: React.FC = () => {
  const [currentTip, setCurrentTip] = useState<LogisticsTip | null>(null);
  const [collectedTips, setCollectedTips] = useState<LogisticsTip[]>([]);
  const [showCollection, setShowCollection] = useState(false);
  const [tipSaved, setTipSaved] = useState(false);
  const [isNewTip, setIsNewTip] = useState(false);
  const [collapsed, setCollapsed] = useState(false);

  // Generate a random tip based on rarity probabilities
  const generateRandomTip = (): LogisticsTip => {
    // Determine rarity based on probability
    const rarityRoll = Math.random() * 100;
    let rarity: TipRarity;
    
    if (rarityRoll < 3) {
      rarity = TipRarity.LEGENDARY; // 3% chance
    } else if (rarityRoll < 10) {
      rarity = TipRarity.EPIC; // 7% chance
    } else if (rarityRoll < 25) {
      rarity = TipRarity.RARE; // 15% chance
    } else if (rarityRoll < 50) {
      rarity = TipRarity.UNCOMMON; // 25% chance
    } else {
      rarity = TipRarity.COMMON; // 50% chance
    }
    
    // Filter tips by rarity
    const tipsOfRarity = logisticsTips.filter(tip => tip.rarity === rarity);
    
    // Select a random tip from the filtered list
    const randomIndex = Math.floor(Math.random() * tipsOfRarity.length);
    return tipsOfRarity[randomIndex];
  };

  // Load collected tips from local storage
  useEffect(() => {
    const savedTips = localStorage.getItem('collectedLogisticsTips');
    if (savedTips) {
      setCollectedTips(JSON.parse(savedTips));
    }
    
    // Generate initial random tip
    setCurrentTip(generateRandomTip());
  }, []);

  // Save collected tips to local storage whenever the collection changes
  useEffect(() => {
    localStorage.setItem('collectedLogisticsTips', JSON.stringify(collectedTips));
  }, [collectedTips]);

  const handleGenerateNewTip = () => {
    setCurrentTip(generateRandomTip());
    setTipSaved(false);
    setIsNewTip(true);
    
    // Reset animation after a short delay
    setTimeout(() => {
      setIsNewTip(false);
    }, 1000);
  };

  const handleSaveTip = () => {
    if (currentTip && !collectedTips.some(tip => tip.id === currentTip.id)) {
      setCollectedTips([...collectedTips, currentTip]);
      setTipSaved(true);
    }
  };

  const toggleCollection = () => {
    setShowCollection(!showCollection);
  };

  const toggleCollapsed = () => {
    setCollapsed(!collapsed);
  };

  if (!currentTip) {
    return <div className="p-2 text-center text-xs">Cargando consejos...</div>;
  }

  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden">
      <div 
        className="p-3 bg-gradient-to-r from-primary to-primary-dark text-white cursor-pointer"
        onClick={toggleCollapsed}
      >
        <div className="flex justify-between items-center">
          <h2 className="text-base font-bold flex items-center">
            <Award className="mr-1.5" size={16} />
            Consejos de Logística
          </h2>
          <button className="p-1 rounded-full hover:bg-primary-dark transition-colors">
            {collapsed ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
          </button>
        </div>
      </div>

      {!collapsed && (
        <>
          {!showCollection ? (
            <div className={`p-3 bg-gradient-to-b ${rarityGradients[currentTip.rarity]} border-t-2 border-b-2 ${rarityColors[currentTip.rarity].split(' ')[1]} ${rarityTextColors[currentTip.rarity]} ${rarityShadows[currentTip.rarity]} transition-all duration-300 ${isNewTip ? 'scale-105' : 'scale-100'}`}>
              <div className="flex justify-between items-start mb-2">
                <span className={`text-xs font-semibold px-1.5 py-0.5 rounded-full ${rarityBadgeColors[currentTip.rarity]}`}>
                  {currentTip.rarity}
                </span>
                <span className="text-xs text-gray-500">#{currentTip.id}</span>
              </div>
              
              <h3 className="text-sm font-bold mb-2">{currentTip.title}</h3>
              
              <div className="mb-3 rounded-lg overflow-hidden shadow-sm">
                <img 
                  src={currentTip.imageUrl} 
                  alt={currentTip.title} 
                  className="w-full h-32 object-cover"
                />
              </div>
              
              <p className="text-xs mb-3 line-clamp-3">{currentTip.description}</p>
              
              <div className="flex justify-between">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSaveTip();
                  }}
                  disabled={tipSaved || collectedTips.some(tip => tip.id === currentTip.id)}
                  className={`flex items-center px-2 py-1 rounded-md text-xs ${
                    tipSaved || collectedTips.some(tip => tip.id === currentTip.id)
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-primary text-white hover:bg-primary-dark'
                  }`}
                >
                  <Save size={12} className="mr-1" />
                  {tipSaved || collectedTips.some(tip => tip.id === currentTip.id)
                    ? 'Guardado'
                    : 'Guardar'}
                </button>
                
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleGenerateNewTip();
                  }}
                  className="flex items-center px-2 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-xs"
                >
                  <RefreshCw size={12} className="mr-1" />
                  Nuevo
                </button>
              </div>
            </div>
          ) : (
            <div className="p-3">
              <h3 className="text-sm font-bold mb-2">Tu Colección ({collectedTips.length})</h3>
              
              {collectedTips.length === 0 ? (
                <div className="text-center py-3 text-xs text-gray-500">
                  <Award size={24} className="mx-auto mb-1 opacity-30" />
                  <p>Aún no has guardado ningún consejo.</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-2 max-h-64 overflow-y-auto pr-1">
                  {collectedTips.map(tip => (
                    <div 
                      key={tip.id}
                      className={`p-2 rounded-md border ${rarityColors[tip.rarity]} flex items-start bg-gradient-to-r ${rarityGradients[tip.rarity]} ${rarityShadows[tip.rarity]}`}
                    >
                      <img 
                        src={tip.imageUrl} 
                        alt={tip.title}
                        className="w-10 h-10 object-cover rounded-md mr-2 flex-shrink-0 shadow-sm"
                      />
                      <div className="flex-grow overflow-hidden">
                        <div className="flex justify-between items-start mb-1">
                          <span className={`text-xs font-semibold px-1 py-0.5 rounded-full ${rarityBadgeColors[tip.rarity]}`}>
                            {tip.rarity}
                          </span>
                          <span className="text-xs text-gray-500">#{tip.id}</span>
                        </div>
                        <h4 className={`font-semibold text-xs truncate ${rarityTextColors[tip.rarity]}`}>{tip.title}</h4>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          
          <div className="p-2 bg-gray-50 border-t border-gray-200">
            <div className="flex justify-between items-center">
              <div className="text-xs text-gray-600">
                <span className="font-semibold">{collectedTips.length}</span> consejos
              </div>
              
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  toggleCollection();
                }}
                className="flex items-center text-primary hover:text-primary-dark text-xs"
              >
                {showCollection ? (
                  <>
                    <RefreshCw size={12} className="mr-1" />
                    Ver Consejos
                  </>
                ) : (
                  <>
                    <BookOpen size={12} className="mr-1" />
                    Ver Colección
                  </>
                )}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default LogisticsTips;